import React, { useEffect, useState } from "react";
import Select from "react-select";
import { Card, Col, Container, Form, Row } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";

// Project Imports
import NoIndexSEO from "../../../components/SEO/NoIndexSEO";
import { seoConfig } from "../../../utils/seoConfig";
import {
  publicGetSalaries,
  salariesDataAction,
} from "../../../redux/slices/salariesSlice";
import "./salaries.scss";
import { ReactComponent as FiSliders } from "../../../assets/images/filters-icon.svg";
import LoginRequiredModal from "./LoginRequiredModal";
import { useScreenTime } from "../../../hooks/useScreen";
import usePlan from "../../../hooks/usePlan";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
// import { trackActivity } from "../../../redux/slices/trackActivitySlice";

const Salaries = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const user = useSelector((state) => state.auth?.user);
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
  // True for any paid plan (Plus grandfathered, Pro, Pro Trial, Pro+Newsletter).
  const { isPaid: isProUser } = usePlan();
  const handleCloseSubscription = () => {
    setShowSubscriptionModal(false);
  };
  const [showScrollPopup, setShowScrollPopup] = useState(false);
  const [hasClosedPopup, setHasClosedPopup] = useState(false);


  // useEffect(() => {
  //   const MAX_VISITS = 2;
  //   const STORAGE_KEY = "salaryPageVisitCount";

  //   let visitCount = Number(localStorage.getItem(STORAGE_KEY)) || 0;

  //   if (visitCount >= MAX_VISITS) {
  //     alert("You have completed the count. Please login to continue.");
  //     navigate("/login");
  //     return;
  //   }

  //   // increment visit count
  //   localStorage.setItem(STORAGE_KEY, visitCount + 1);
  // }, [navigate]);
  useEffect(() => {
    const MAX_VISITS = 2;
    const STORAGE_KEY = "salaryPageVisitCount";

    let visitCount = Number(localStorage.getItem(STORAGE_KEY)) || 0;

    if (visitCount >= MAX_VISITS) {
      const showLimitModal = async () => {
        try {
          await Swal.fire({
            icon: "info",
            title: "Visit limit reached",
            text: "You have completed your free visits. Please login to continue.",
            timer: 3000,
            showConfirmButton: false,
            customClass: {
              popup: "success-popup-modal",
            },
          });

          navigate("/login");
        } catch (e) {
          console.error("Swal error:", e);
          navigate("/login"); // fallback
        }
      };

      showLimitModal();
      return;
    }

    // increment visit count
    localStorage.setItem(STORAGE_KEY, visitCount + 1);
  }, [navigate]);


  const SALARY_API_COUNT_KEY = "salary_api_count";
  const MAX_FREE_CALLS = 2;
  const getApiCount = () => {
    return Number(localStorage.getItem(SALARY_API_COUNT_KEY)) || 0;
  };
  const isLimitReached = !user?.id && getApiCount() >= MAX_FREE_CALLS;

  const incrementApiCount = () => {
    const count = getApiCount() + 1;
    localStorage.setItem(SALARY_API_COUNT_KEY, count);
    return count;
  };

  const resetApiCount = () => {
    localStorage.removeItem(SALARY_API_COUNT_KEY);
  };
  useEffect(() => {
    if (user?.id) {
      resetApiCount();
    }
  }, [user]);

  // -- State Management --
  const [tooltip, setTooltip] = useState({
    visible: false,
    x: 0,
    y: 0,
    data: null,
    placement: "above",
    horizontalPlacement: "right",
  });

  const [salariesData, setSalariesData] = useState([]);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [taxInputState, setTaxinputState] = useState({});

  const saved = JSON.parse(localStorage.getItem("filters"));

  // -- Filters State --
  const [filters, setFilters] = useState({
    page_type: "salary",
    page: 1,
    length: 10,
    years_of_experience: 5,
    position: "Night Shift",
    over_time: 0,
    double_time: 0,
    deductions: {},
    ...(saved || {}),
    // Always default these on initial load regardless of saved values
    rate_per: "Month",
    hours_per_week: 40,
  });

  // -- Redux Data --
  const taxInputsData = useSelector((state) => state.salaries);

  // 1. UPDATE: Sync Redux data to Local State
  useEffect(() => {
    if (taxInputsData?.taxInputData) {
      setTaxinputState(taxInputsData.taxInputData);
    }
  }, [taxInputsData]);

  // 2. UPDATE: Update Filters based on Tax Inputs
  useEffect(() => {
    setFilters((prevFilters) => ({
      ...prevFilters,
      deductions: taxInputState?.deductions || {},
      rate_per: taxInputState?.rate_per || "Month",
    }));
  }, [taxInputState]);

  const show401k =
    taxInputState?.deductions &&
    Object.keys(taxInputState.deductions).includes("401k");

  const dropdownOptions = {
    rate_per: ["Hour", "Week", "Bi-Weekly", "Month", "Year"],
    hours_per_week: [4, 8, 12, 16, 20, 24, 28, 30, 32, 36, 40],
    years_of_experience: [0, 1, 2, 3, 4, 5, 10, 15, 20, 25, 30],
    position: [
      "Base",
      "Evening Shift",
      "Night Shift",
      "Weekend",
      "Weekend Evenings",
      "Weekend Nights",
      "Per Diem Days",
      "Per Diem Evenings",
      "Per Diem Nights",
    ],
    over_time: [0, 4, 8, 12, 16, 20, 24, 28, 30, 32, 36, 40],
    double_time: [0, 4, 8, 12, 16, 20, 24, 28, 30, 32, 36, 40],
  };

  useEffect(() => {
    dispatch(salariesDataAction(filters));
  }, [filters, dispatch]);

  useEffect(() => {
    const fetchSalaries = async () => {
      try {
        setLoadingMore(true);

        localStorage.setItem(
          "filters",
          JSON.stringify({
            ...filters,
            page: 1,
            deductions: taxInputState?.deductions,
          })
        );

        const res = await dispatch(publicGetSalaries(filters));

        const docs = res?.payload?.data?.docs || [];
        const totalPages = res?.payload?.data?.pages || 1;

        if (filters.page === 1) {
          setSalariesData(docs);
        } else {
          setSalariesData((prev) => [...prev, ...docs]);
        }

        setHasMore(filters.page < totalPages);
        setLoadingMore(false);
      } catch (error) {
        setLoadingMore(false);
      }
    };

    fetchSalaries();
  }, [filters, taxInputState?.deductions, dispatch]);


  useEffect(() => {
    const handleScroll = () => {
      const bottomReached =
        window.innerHeight + window.scrollY >= document.body.offsetHeight - 100;

      if (bottomReached && hasMore && !loadingMore) {
        setFilters((prev) => {
          if (prev.page === filters.page) {
            return {
              ...prev,
              page: prev.page + 1,
            };
          }
          return prev;
        });
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [hasMore, loadingMore, filters.page]);
  useEffect(() => {
    const handleScrollPopup = () => {
      if (hasClosedPopup) return;

      // Only free users should see this popup; skip it for any paid plan.
      if (isProUser) return;

      const scrollPosition = window.scrollY;
      const pageHeight = document.body.scrollHeight - window.innerHeight;

      if (scrollPosition / pageHeight > 0.35 && !showScrollPopup) {
        setShowScrollPopup(true);
      }
    };

    window.addEventListener("scroll", handleScrollPopup);
    return () => window.removeEventListener("scroll", handleScrollPopup);
  }, [showScrollPopup, hasClosedPopup, isProUser]);

  const formatCurrency = (value) => {
    if (value === undefined || value === null || isNaN(value)) return "$0.00";
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);
  };

  const showTooltip = (evt, item) => {
    const rect = evt.currentTarget.getBoundingClientRect();
    const vw = window.innerWidth;
    const vh = window.innerHeight;

    const pad = 12;
    const tooltipWidth = 280;
    const xClient = evt.clientX;
    const yClient = evt.clientY;

    const availableTop = yClient;
    const availableBottom = vh - yClient;
    const placement =
      availableBottom < 180 && availableTop > availableBottom
        ? "above"
        : "below";

    const spaceRight = vw - rect.right;
    const spaceLeft = rect.left;

    let horizontalPlacement;
    if (spaceRight > tooltipWidth) {
      horizontalPlacement = "right";
    } else if (spaceLeft > tooltipWidth) {
      horizontalPlacement = "left";
    } else {
      horizontalPlacement = "center";
    }

    let x;
    if (horizontalPlacement === "right") {
      x = Math.min(vw - pad - tooltipWidth, xClient + 16);
    } else if (horizontalPlacement === "left") {
      x = Math.max(pad + tooltipWidth, xClient - 16);
    } else {
      x = Math.max(
        pad + tooltipWidth / 2,
        Math.min(vw - pad - tooltipWidth / 2, xClient),
      );
    }

    const y = placement === "below" ? yClient + 12 : yClient - 12;
    setTooltip({
      visible: true,
      x,
      y,
      data: item,
      placement,
      horizontalPlacement,
    });
  };

  const moveTooltip = (evt) => {
    if (!tooltip.visible) return;
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const pad = 12;
    const tooltipWidth = 280;
    const xClient = evt.clientX;
    const yClient = evt.clientY;

    let x;
    if (tooltip.horizontalPlacement === "right") {
      x = Math.min(vw - pad - tooltipWidth, xClient + 16);
    } else if (tooltip.horizontalPlacement === "left") {
      x = Math.max(pad + tooltipWidth, xClient - 16);
    } else {
      x = Math.max(
        pad + tooltipWidth / 2,
        Math.min(vw - pad - tooltipWidth / 2, xClient),
      );
    }

    const availableTop = yClient;
    const availableBottom = vh - yClient;
    const placement =
      availableBottom < 180 && availableTop > availableBottom
        ? "above"
        : "below";
    const y = placement === "below" ? yClient + 12 : yClient - 12;

    setTooltip((t) => ({ ...t, x, y, placement }));
  };

  const hideTooltip = () =>
    setTooltip({ visible: false, x: 0, y: 0, data: null });

  const maxSalary =
    salariesData?.length > 0
      ? Math.max(...salariesData.map((s) => s.grossPay))
      : 1;


  const handleFilterChange = (field, value) => {
    const count = getApiCount();

    // 🔐 SAME behavior as old isProUser logic
    if (count >= MAX_FREE_CALLS) {
      setShowSubscriptionModal(true);
      return; //  stop filter change & API
    }

    // ✅ allow filter change
    incrementApiCount();

    setFilters((prev) => ({
      ...prev,
      [field]: value,
      page: 1,
    }));
  };


  const topHospital = salariesData?.length > 0 ? salariesData[0] : null;

  const selectStyles = {
    menu: (base) => ({
      ...base,
      borderRadius: "15px",
      marginTop: "8px",
      backgroundColor: "#fff",
      padding: "6px",
      zIndex: "1000",
    }),
    menuPortal: (base) => ({
      ...base,
      zIndex: 9999,
    }),
    option: (base, state) => ({
      ...base,
      borderRadius: "8px",
      padding: "10px",
      backgroundColor: state.isFocused ? "#F3F9FC" : "#fff",
      color: state.isDisabled ? "#999" : "#091F28",
      cursor: state.isDisabled ? "not-allowed" : "pointer",
      opacity: state.isDisabled ? 0.6 : 1,
      "&:active": {
        backgroundColor: state.isDisabled ? "#fff" : "#F3F9FC",
      },
    }),
  };

  return (
    <Container className="py-4">
      <Row className="justify-content-center">
        <div className="filters-content-main salaries-filters">
          {/* ... [Keep Sidebar and UI exactly as is] ... */}
          <NoIndexSEO title={seoConfig.salaries.title} />
          <Row className="mb-3">
            {/* -- Sidebar -- */}
            <div className="filter-sidebar filterSideBar-Custom">
              {/* ... Sidebar Code ... */}
              <Card className="filter-container mb-0 custom-filter-Container">
                <div className="inner-sub-sub-title">
                  <div className="inner-sub-title">Salary Calculator</div>
                  <button
                    className="filter-toggle-btn"
                    onClick={() => setShowFilters(!showFilters)}
                  >
                    <FiSliders />
                  </button>
                </div>
                <div className={`filter ${showFilters ? "open" : "closed"}`}>
                  <div className="select-boxes">
                    <div className="filter-section">
                      <Form.Group className="form-group mb-0">
                        <Form.Label className="col-label">Rate Per</Form.Label>
                        <Select
                          value={{
                            value: filters.rate_per,
                            label: filters.rate_per,
                          }}
                          onChange={(option) => {
                            handleFilterChange("rate_per", option.value);
                          }}
                          placeholder="Select rate"
                          options={dropdownOptions.rate_per.map((item) => ({
                            value: item,
                            label: item,
                          }))}
                          // Remove isDisabled={...} ← let user click it!
                          className="react-select-container"
                          classNamePrefix="react-select"
                          menuPosition="fixed"
                          styles={selectStyles}
                        />
                      </Form.Group>
                    </div>
                    <div className="filter-section">
                      <Form.Group className="form-group mb-0">
                        <Form.Label className="col-label">
                          Hours Per Week
                        </Form.Label>
                        <Select
                          value={{
                            value: filters.hours_per_week,
                            label: filters.hours_per_week,
                          }}

                          onChange={(option) => {
                            if (!isProUser) {
                              setShowSubscriptionModal(true);
                              return;
                            }
                            handleFilterChange("hours_per_week", option.value);
                          }}
                          // placeholder="Select Hours Per Week"
                          placeholder={
                            isProUser
                              ? "Select Hours Per Week"
                              : "Upgrade to Pro"
                          }
                          className="react-select-container"
                          classNamePrefix="react-select"
                          options={dropdownOptions.hours_per_week.map(
                            (item) => ({
                              value: item,
                              label: item,
                            }),
                          )}
                          menuPosition="fixed"
                          styles={selectStyles}
                        />
                      </Form.Group>
                    </div>
                    <div className="filter-section">
                      <Form.Group className="form-group mb-0">
                        <Form.Label className="col-label">
                          Years of Exp
                        </Form.Label>
                        <Select
                          value={{
                            value: filters.years_of_experience,
                            label: filters.years_of_experience,
                          }}
                          onChange={(option) =>
                            handleFilterChange(
                              "years_of_experience",
                              option.value,
                            )
                          }

                          placeholder="Select Years of Exp"

                          className="react-select-container"
                          classNamePrefix="react-select"
                          options={dropdownOptions.years_of_experience.map(
                            (item) => ({ value: item, label: item }),
                          )}
                          menuPosition="fixed"
                          styles={selectStyles}
                        />
                      </Form.Group>
                    </div>
                    <div className="filter-section">
                      <Form.Group className="form-group mb-0">
                        <Form.Label className="col-label">Position</Form.Label>
                        <Select
                          value={{
                            value: filters.position,
                            label: filters.position,
                          }}
                          onChange={(option) =>
                            handleFilterChange("position", option.value)
                          }

                          placeholder="Select Position"
                          className="react-select-container"
                          classNamePrefix="react-select"
                          options={dropdownOptions.position.map((item) => ({
                            value: item,
                            label: item,
                          }))}
                          menuPosition="fixed"
                          styles={selectStyles}
                        />
                      </Form.Group>
                    </div>
                    <div className="filter-section">
                      <Form.Group className="form-group mb-0">
                        <Form.Label className="col-label">Over Time</Form.Label>
                        <Select
                          value={{
                            value: filters.over_time,
                            label: filters.over_time,
                          }}
                          onChange={(option) =>
                            handleFilterChange("over_time", option.value)
                          }
                          placeholder="Select Over Time"

                          className="react-select-container"
                          classNamePrefix="react-select"
                          options={dropdownOptions.over_time.map((item) => ({
                            value: item,
                            label: item,
                          }))}
                          menuPosition="fixed"
                          styles={selectStyles}
                        />
                      </Form.Group>
                    </div>
                    <div className="filter-section">
                      <Form.Group className="form-group mb-0">
                        <Form.Label className="col-label">
                          Double Time
                        </Form.Label>
                        <Select
                          value={{
                            value: filters.double_time,
                            label: filters.double_time,
                          }}
                          onChange={(option) =>
                            handleFilterChange("double_time", option.value)
                          }
                          placeholder="Select Double Time"

                          className="react-select-container"
                          classNamePrefix="react-select"
                          options={dropdownOptions.double_time.map((item) => ({
                            value: item,
                            label: item,
                          }))}
                          menuPosition="fixed"
                          styles={selectStyles}
                        />
                      </Form.Group>
                    </div>
                  </div>
                </div>
              </Card>
            </div>

            {/* -- Content Area -- */}
            <div className="filter-content">
              <Card className="mb-0 custom-salry-card">
                <Card.Body>
                  <div className="inner-sub-sub-title">
                    <div className="inner-sub-title">
                      Hospital Salary Rankings
                    </div>
                  </div>

                  {!salariesData ? (
                    <p className="no-results">Loading...</p>
                  ) : salariesData.length === 0 ? (
                    <p className="no-results">
                      No data available for selected criteria
                    </p>
                  ) : (
                    <div className="salary-list">
                      {salariesData.map((result, index) => {
                        const k401Raw =
                          result.taxes.deductionsMonthly?.["401k"] || 0;

                        const tooltipData = {
                          hospital: result.hospital,
                          gross: result.taxes.gross,
                          federalTax: result.taxes.federalTax,
                          stateTax: result.taxes.stateTax,
                          ssTax: result.taxes.ssTax,
                          medicareTax: result.taxes.medicareTax,
                          sdiTax: result.taxes.sdiTax,
                          k401: k401Raw,
                          net: result.taxes.net,
                          totalDeductions:
                            result.taxes.federalTax +
                            result.taxes.stateTax +
                            result.taxes.ssTax +
                            result.taxes.sdiTax +
                            result.taxes.medicareTax +
                            k401Raw || 0,
                        };

                        return (
                          <div
                            key={result.hospital || index}
                            className="salary-item"
                            onMouseEnter={(e) => showTooltip(e, tooltipData)}
                            onMouseMove={moveTooltip}
                            onMouseLeave={hideTooltip}
                          >
                            <div className="hospital-name">
                              {result.hospital}
                            </div>
                            <div className="salary-bar-container">
                              <div
                                className="salary-bar"
                                style={{
                                  width: `${(result.grossPay / maxSalary) *
                                    100}%`,
                                }}
                              ></div>
                            </div>
                            <div className="salary-amount">
                              {formatCurrency(result.grossPay)}
                            </div>
                          </div>
                        );
                      })}
                      {loadingMore && (
                        <p className="text-center mt-3">
                          Loading more hospitals...
                        </p>
                      )}
                    </div>
                  )}

                  {/* -- Tooltip -- */}
                  {tooltip.visible && tooltip.data && (
                    <div
                      className={`salary-tooltip ${tooltip.placement === "below" ? "below" : ""
                        } ${tooltip.horizontalPlacement}`}
                      style={{ left: tooltip.x, top: tooltip.y }}
                      role="dialog"
                      aria-label={`Breakdown for ${tooltip.data.hospital}`}
                    >
                      <div className="tooltip-title">
                        {tooltip.data.hospital}
                      </div>
                      <div className="tooltip-row">
                        <span>Gross:</span>
                        <strong>{formatCurrency(tooltip.data.gross)}</strong>
                      </div>
                      <div className="tooltip-row negative">
                        <span>Federal Tax:</span>
                        <strong>
                          -{formatCurrency(tooltip.data.federalTax)}
                        </strong>
                      </div>
                      <div className="tooltip-row negative">
                        <span>State Tax:</span>
                        <strong>
                          -{formatCurrency(tooltip.data.stateTax)}
                        </strong>
                      </div>
                      <div className="tooltip-row negative">
                        <span>SS Tax:</span>
                        <strong>-{formatCurrency(tooltip.data.ssTax)}</strong>
                      </div>
                      <div className="tooltip-row negative">
                        <span>Medicare:</span>
                        <strong>
                          -{formatCurrency(tooltip.data.medicareTax)}
                        </strong>
                      </div>
                      <div className="tooltip-row negative">
                        <span>SDI:</span>
                        <strong>-{formatCurrency(tooltip.data.sdiTax)}</strong>
                      </div>

                      {/* 4. UPDATE: Logic to conditionally Render 401k
                     We use the show401k flag derived from taxInputState
                  */}
                      {show401k && (
                        <div className="tooltip-row negative">
                          <span>401k:</span>
                          <strong>-{formatCurrency(tooltip.data.k401)}</strong>
                        </div>
                      )}

                      <div className="tooltip-sep" />
                      <div className="tooltip-row negative">
                        <span>Total Deductions:</span>
                        <strong>
                          -{formatCurrency(tooltip.data.totalDeductions)}
                        </strong>
                      </div>
                      <div className="tooltip-row net">
                        <span>Net Pay:</span>
                        <strong>{formatCurrency(tooltip.data.net)}</strong>
                      </div>
                    </div>
                  )}
                </Card.Body>
              </Card>
            </div>
          </Row>

          {/* ... [Keep Salary Summary Card exactly as is] ... */}
          <Card className="mb-0 salaries-card">
            <Card.Body>
              {/* ... Summary Content ... */}
              <div className="inner-sub-sub-title">
                <div className="inner-sub-title">Salary Summary</div>
              </div>

              <Card className="investment-stats mb-0 card">
                <Row className="text-center">
                  <Col className="col-12 col-lg-3">
                    <div className="investment-stats-wrapper">
                      <div className="stat-label">Top Hospital</div>
                      <div className="stat-value">
                        {topHospital ? topHospital.hospital : "N/A"}
                      </div>
                    </div>
                  </Col>

                  <Col className="col-12 col-lg-3">
                    <div className="investment-stats-wrapper">
                      <div className="stat-label">Highest Salary</div>
                      <div className="stat-value">
                        {/* NO MULTIPLIER: Use backend grossPay directly */}
                        {topHospital
                          ? formatCurrency(topHospital.grossPay)
                          : "N/A"}
                      </div>
                    </div>
                  </Col>

                  <Col className="col-12 col-lg-3">
                    <div className="investment-stats-wrapper">
                      <div className="stat-label">Est. Net Pay</div>
                      <div className="stat-value">
                        {/* NO MULTIPLIER: Use backend net directly */}
                        {topHospital
                          ? formatCurrency(topHospital.taxes.net)
                          : "N/A"}
                      </div>
                    </div>
                  </Col>

                  <Col className="col-12 col-lg-3">
                    <div className="investment-stats-wrapper">
                      <div className="stat-label">Hourly Rate</div>
                      <div className="stat-value">
                        {topHospital
                          ? `${formatCurrency(topHospital.hourlyRate)}/hour`
                          : "N/A"}
                      </div>
                    </div>
                  </Col>
                </Row>
              </Card>
            </Card.Body>
          </Card>

          <LoginRequiredModal
            show={showSubscriptionModal}
            onClose={() => setShowSubscriptionModal(false)}
          />

          {showScrollPopup && (
            <div className={`scroll-popup ${showScrollPopup ? "visible" : ""}`}>
              <div className="popup-inner">
                <div className="popup-header">
                  <h3 className="popup-title">
                    Get top hospital jobs in your inbox
                  </h3>
                  <button
                    className="popup-close"
                    onClick={() => {
                      setShowScrollPopup(false);
                      setHasClosedPopup(true);
                    }}
                    aria-label="Close"
                  >
                    ×
                  </button>
                </div>

                <p className="popup-subtext">
                  Want salary-leading hospital roles delivered weekly?
                  <br />
                  Subscribe to our curated job listings newsletter for just{" "}
                  <strong>$4.99/mo</strong>.
                </p>

                <div className="popup-actions">
                  <button
                    className="subscribe-btn"
                    onClick={() => {
                      window.location.href =
                        "https://newsletter.mapmypay.com/upgrade";
                    }}
                  >
                    Subscribe for $4.99/mo
                  </button>

                  <button
                    className="later-btn"
                    onClick={() => {
                      setShowScrollPopup(false);
                      setHasClosedPopup(true);
                    }}
                  >
                    Not now
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </Row>
    </Container>
  );
};

export default Salaries;
